import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import "../styles/aljabriyah-assistant.css";

const STORAGE_KEY = "aljabriyah_ai_chat";

export default function AlJabriyahAssistant({ language = "ar" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const location = useLocation();
const [assistantPosition, setAssistantPosition] = useState({
  x: window.innerWidth - 100,
  y: window.innerHeight - 100,
});

const dragOffsetRef = useRef({ x: 0, y: 0 });
const isDraggingRef = useRef(false);
const dragStartRef = useRef({ x: 0, y: 0 });
const didDragRef = useRef(false);
  const isArabic = language === "ar";

  const content = {
    ar: {
      title: "مساعد الجبرية الذكي",
      welcome: "مرحبًا بك في الجبرية! كيف يمكنني مساعدتك اليوم؟",
      placeholder: "اكتب سؤالك هنا...",
      send: "إرسال",
      thinking: "جاري الكتابة...",
      clear: "محادثة جديدة",
      error: "واجهت مشكلة مؤقتة. حاول مرة أخرى.",
      quickQuestions: [
        "ما الفرق بين الدراسة السوقية والتشغيلية؟",
        "أي دراسة تناسب مشروعي؟",
        "كيف أبدأ؟",
        "ساعدني في فهم الاستبيان",
      ],
    },

    en: {
      title: "Al-Jabriyah AI Assistant",
      welcome: "Welcome to Al-Jabriyah! How can I help you today?",
      placeholder: "Type your question...",
      send: "Send",
      thinking: "Typing...",
      clear: "New chat",
      error: "I encountered a temporary problem. Please try again.",
      quickQuestions: [
        "What is the difference between the two studies?",
        "Which study is right for my project?",
        "How do I get started?",
        "Help me understand the survey",
      ],
    },
  };

  const t = content[isArabic ? "ar" : "en"];

  // Load saved conversation
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);

      if (saved) {
        const parsed = JSON.parse(saved);

        if (Array.isArray(parsed)) {
          setMessages(parsed);
        }
      }
    } catch (error) {
      console.error("Failed to load assistant history:", error);
    }
  }, []);

  // Save conversation
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (error) {
      console.error("Failed to save assistant history:", error);
    }
  }, [messages]);

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, isLoading, isOpen]);

  const detectMessageLanguage = (text) => {
    return /[\u0600-\u06FF]/.test(text) ? "ar" : "en";
  };

  const getPageContext = () => {
    const path = location.pathname.toLowerCase();

    if (path.includes("marketsurvey")) {
      return "market_survey";
    }

    if (
      path.includes("technical-survey") ||
      path.includes("technicalsurvey")
    ) {
      return "technical_operational_survey";
    }

    if (path.includes("feasibility")) {
      return "feasibility_services";
    }

    if (path.includes("payment")) {
      return "payment";
    }

    if (path.includes("dashboard")) {
      return "client_dashboard";
    }

    return "general_website";
  };

  const clearConversation = () => {
    setMessages([]);
    setMessage("");
    localStorage.removeItem(STORAGE_KEY);
  };

  const sendMessage = async (e, quickText = null) => {
    if (e) {
      e.preventDefault();
    }

    if (isLoading) return;

    const text = (quickText ?? message).trim();

    if (!text) return;

    const detectedLanguage = detectMessageLanguage(text);

    const previousMessages = messages.slice(-8);

    const userMessage = {
      role: "user",
      text,
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setIsLoading(true);

    try {
      console.log(
        "ASSISTANT MESSAGE LANGUAGE:",
        detectedLanguage
      );

      const response = await fetch(
        "http://127.0.0.1:8000/assistant/chat",
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            message: text,
            language: detectedLanguage,
            page_context: getPageContext(),
            page_path: location.pathname,
            history: previousMessages,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      const assistantReply =
        data.reply ||
        (detectedLanguage === "ar"
          ? "لم أتمكن من إنشاء رد."
          : "I could not generate a response.");

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: assistantReply,
        },
      ]);
    } catch (error) {
      console.error("Assistant error:", error);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text:
            detectedLanguage === "ar"
              ? content.ar.error
              : content.en.error,
          isError: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };
const handleDragStart = (e) => {
  const point = e.touches ? e.touches[0] : e;

  isDraggingRef.current = true;
  didDragRef.current = false;

  dragStartRef.current = {
    x: point.clientX,
    y: point.clientY,
  };

  dragOffsetRef.current = {
    x: point.clientX - assistantPosition.x,
    y: point.clientY - assistantPosition.y,
  };
};

const handleDragMove = (e) => {
  if (!isDraggingRef.current) return;

  const point = e.touches ? e.touches[0] : e;

  const deltaX = Math.abs(
    point.clientX - dragStartRef.current.x
  );

  const deltaY = Math.abs(
    point.clientY - dragStartRef.current.y
  );

  // إذا تحرك أكثر من 5px نعتبرها Drag حقيقي
  if (deltaX > 5 || deltaY > 5) {
    didDragRef.current = true;
  }

  const buttonSize = 70;

  const newX = Math.min(
    Math.max(
      10,
      point.clientX - dragOffsetRef.current.x
    ),
    window.innerWidth - buttonSize - 10
  );

  const newY = Math.min(
    Math.max(
      10,
      point.clientY - dragOffsetRef.current.y
    ),
    window.innerHeight - buttonSize - 10
  );

  setAssistantPosition({
    x: newX,
    y: newY,
  });
};

const handleDragEnd = () => {
  isDraggingRef.current = false;
};
useEffect(() => {
  window.addEventListener("mousemove", handleDragMove);
  window.addEventListener("mouseup", handleDragEnd);

  window.addEventListener("touchmove", handleDragMove, {
    passive: false,
  });

  window.addEventListener("touchend", handleDragEnd);

  return () => {
    window.removeEventListener("mousemove", handleDragMove);
    window.removeEventListener("mouseup", handleDragEnd);

    window.removeEventListener("touchmove", handleDragMove);
    window.removeEventListener("touchend", handleDragEnd);
  };
}, [assistantPosition]);
const getChatWindowStyle = () => {
  const buttonSize = 70;
  const gap = 12;
  const margin = 10;

  const chatWidth = Math.min(440, window.innerWidth - 28);
  const chatHeight = Math.min(680, window.innerHeight - 105);

  // نخلي الشات بمحاذاة زر AI
  let left =
    assistantPosition.x + buttonSize - chatWidth;

  // منع خروجه من يمين أو يسار الشاشة
  left = Math.max(
    margin,
    Math.min(
      left,
      window.innerWidth - chatWidth - margin
    )
  );

  // إذا في مساحة فوق الزر، افتح الشات فوقه
  // وإذا ما في، افتحه تحته
  let top;

  if (
    assistantPosition.y >
    chatHeight + gap + margin
  ) {
    top =
      assistantPosition.y -
      chatHeight -
      gap;
  } else {
    top =
      assistantPosition.y +
      buttonSize +
      gap;
  }

  // منع خروجه من أسفل الشاشة
  top = Math.max(
    margin,
    Math.min(
      top,
      window.innerHeight -
        chatHeight -
        margin
    )
  );

  return {
    position: "fixed",
    left: `${left}px`,
    top: `${top}px`,
    right: "auto",
    bottom: "auto",
    width: `${chatWidth}px`,
    height: `${chatHeight}px`,
    zIndex: 9998,
  };
};
  return (
    <div
      className="jabriyah-assistant"
      dir={isArabic ? "rtl" : "ltr"}
    >
      {isOpen && (
        <section
  className="jabriyah-chat-window"
  role="dialog"
  aria-label={t.title}
  style={getChatWindowStyle()}
>
          <div className="jabriyah-chat-header">
            <div className="jabriyah-chat-brand">
              <span className="jabriyah-chat-avatar">
                AI
              </span>

              <div>
                <strong>{t.title}</strong>

                <span className="jabriyah-chat-status">
                  ● Online
                </span>
              </div>
            </div>

            <div className="jabriyah-chat-header-actions">
              <button
                type="button"
                className="jabriyah-chat-clear"
                onClick={clearConversation}
                aria-label={t.clear}
                title={t.clear}
              >
                ↻
              </button>

              <button
                type="button"
                className="jabriyah-chat-close"
                onClick={() => setIsOpen(false)}
                aria-label={
                  isArabic
                    ? "إغلاق المحادثة"
                    : "Close chat"
                }
              >
                ×
              </button>
            </div>
          </div>

          <div
            className="jabriyah-chat-messages"
            aria-live="polite"
          >
            {messages.length === 0 && (
              <>
                <div className="jabriyah-chat-message assistant">
                  {t.welcome}
                </div>

                <div className="jabriyah-quick-questions">
                  {t.quickQuestions.map(
                    (question, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() =>
                          sendMessage(null, question)
                        }
                        disabled={isLoading}
                      >
                        {question}
                      </button>
                    )
                  )}
                </div>
              </>
            )}

            {messages.map((item, index) => (
              <div
                key={`${item.role}-${index}`}
                className={`jabriyah-chat-message ${
                  item.role
                } ${
                  item.isError ? "error" : ""
                }`}
              >
                {item.text}
              </div>
            ))}

            {isLoading && (
              <div className="jabriyah-chat-message assistant typing">
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>
                <span className="typing-dot"></span>

                <small>{t.thinking}</small>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="jabriyah-chat-bottom">
            <form
              className="jabriyah-chat-form"
              onSubmit={sendMessage}
            >
              <input
                type="text"
                value={message}
                onChange={(e) =>
                  setMessage(e.target.value)
                }
                placeholder={t.placeholder}
                aria-label={t.placeholder}
                maxLength={1000}
                disabled={isLoading}
              />

              <button
                type="submit"
                disabled={
                  !message.trim() || isLoading
                }
              >
                {isLoading ? "..." : t.send}
              </button>
            </form>
          </div>
        </section>
      )}

    <button
  type="button"
  className="jabriyah-chat-toggle draggable"
  onMouseDown={handleDragStart}
  onTouchStart={handleDragStart}
 onClick={(e) => {
  if (didDragRef.current) {
    e.preventDefault();
    didDragRef.current = false;
    return;
  }

  setIsOpen((prev) => !prev);
}}
  aria-label={t.title}
  aria-expanded={isOpen}
  style={{
    position: "fixed",
    left: `${assistantPosition.x}px`,
    top: `${assistantPosition.y}px`,
    right: "auto",
    bottom: "auto",
    zIndex: 9999,
  }}
>
  {isOpen ? "×" : "✦ AI"}
</button>
    </div>
  );
}